import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  PauseIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  XMarkIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { collectorsService, AvailableCollector, CollectorResponse, CollectorStatus } from '../services/collectors';
import CollectorConfigForm from '../components/CollectorConfigForm';

const CollectorsNew: React.FC = () => {
  const [availableCollectors, setAvailableCollectors] = useState<AvailableCollector[]>([]);
  const [configuredCollectors, setConfiguredCollectors] = useState<CollectorResponse[]>([]);
  const [selectedCollector, setSelectedCollector] = useState<AvailableCollector | null>(null);
  const [collectorStatus, setCollectorStatus] = useState<CollectorStatus | null>(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'available' | 'configured'>('available');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [available, configured] = await Promise.all([
        collectorsService.getAvailableCollectors(),
        collectorsService.getCollectors(),
      ]);

      setAvailableCollectors(available);
      setConfiguredCollectors(configured);

      console.log('Available collectors:', available);
      console.log('Configured collectors:', configured);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados dos coletores');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureCollector = (collector: AvailableCollector) => {
    setSelectedCollector(collector);
    setShowConfigModal(true);
  };

  const handleSaveConfig = async (configData: any) => {
    if (!selectedCollector) return;

    try {
      const result = await collectorsService.configureCollector(selectedCollector.type, configData);

      toast.success(result.message);
      setShowConfigModal(false);
      setSelectedCollector(null);
      loadData(); // Reload to update status
    } catch (error: any) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    }
  };

  const handleStartCollector = async (name: string) => {
    try {
      await collectorsService.startCollector(name);
      toast.success(`Coletor ${name} iniciado`);
      loadData();
    } catch (error: any) {
      console.error('Error starting collector:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar coletor');
    }
  };

  const handleStopCollector = async (name: string) => {
    try {
      await collectorsService.stopCollector(name);
      toast.success(`Coletor ${name} parado`);
      loadData();
    } catch (error: any) {
      console.error('Error stopping collector:', error);
      toast.error(error.response?.data?.detail || 'Erro ao parar coletor');
    }
  };

  const handleViewStatus = async (collector: CollectorResponse) => {
    try {
      const status = await collectorsService.getCollectorStatus(collector.name);
      setCollectorStatus(status);
      setShowStatusModal(true);
    } catch (error: any) {
      console.error('Error loading collector status:', error);
      toast.error('Erro ao carregar status do coletor');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'text-green-600 bg-green-100';
      case 'stopped':
        return 'text-gray-600 bg-gray-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      case 'starting':
        return 'text-blue-600 bg-blue-100';
      case 'stopping':
        return 'text-yellow-600 bg-yellow-100';
      case 'not_configured':
        return 'text-gray-500 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Data Leaks':
        return 'bg-red-100 text-red-800';
      case 'Intelligence':
        return 'bg-blue-100 text-blue-800';
      case 'Infrastructure':
        return 'bg-green-100 text-green-800';
      case 'Code Security':
        return 'bg-purple-100 text-purple-800';
      case 'Data Breaches':
        return 'bg-orange-100 text-orange-800';
      case 'Vulnerability':
        return 'bg-yellow-100 text-yellow-800';
      case 'Social Media':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100'>Coletores de Inteligência</h1>
          <p className='text-gray-600'>Configure e gerencie coletores de dados</p>
        </div>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-blue-100 rounded-lg'>
              <Cog6ToothIcon className='h-6 w-6 text-blue-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Disponíveis</p>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>{availableCollectors.length}</p>
            </div>
          </div>
        </div>

        <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-green-100 rounded-lg'>
              <CheckCircleIcon className='h-6 w-6 text-green-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Configurados</p>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                {availableCollectors.filter(c => c.is_configured).length}
              </p>
            </div>
          </div>
        </div>

        <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-emerald-100 rounded-lg'>
              <PlayIcon className='h-6 w-6 text-emerald-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Ativos</p>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                {' '}
                {availableCollectors.filter(c => c.is_enabled).length}
              </p>
            </div>
          </div>
        </div>

        <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-yellow-100 rounded-lg'>
              <XCircleIcon className='h-6 w-6 text-yellow-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Não Configurados</p>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                {' '}
                {availableCollectors.filter(c => !c.is_configured).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className='border-b border-gray-200'>
        <nav className='-mb-px flex space-x-8'>
          <button
            onClick={() => setActiveTab('available')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Coletores Disponíveis
          </button>
          <button
            onClick={() => setActiveTab('configured')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'configured'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Coletores Configurados
          </button>
        </nav>
      </div>

      {/* Available Collectors Tab */}
      {activeTab === 'available' && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {availableCollectors.map(collector => (
            <div
              key={collector.type}
              className='bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow'
            >
              <div className='p-6'>
                <div className='flex items-start justify-between'>
                  <div className='flex items-center'>
                    <div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3'>
                      <span className='text-sm font-bold text-blue-600'>{collector.icon}</span>
                    </div>
                    <div>
                      <h3 className='text-lg font-medium text-gray-900 dark:text-white'>{collector.name}</h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(collector.category)}`}
                      >
                        {collector.category}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(collector.status)}`}
                  >
                    {collector.is_configured ? (collector.is_enabled ? 'Ativo' : 'Inativo') : 'Não Configurado'}
                  </span>
                </div>

                <p className='mt-3 text-sm text-gray-600'>{collector.description}</p>

                <div className='mt-4 flex items-center justify-between'>
                  <div className='flex items-center text-sm text-gray-500'>
                    {collector.requires_api_key && (
                      <span className='inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs font-medium'>
                        API Key Necessária
                      </span>
                    )}
                  </div>

                  <div className='flex space-x-2'>
                    <button
                      onClick={() => handleConfigureCollector(collector)}
                      className='inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    >
                      <CogIcon className='h-4 w-4 mr-1' />
                      {collector.is_configured ? 'Reconfigurar' : 'Configurar'}
                    </button>
                  </div>
                </div>

                {collector.last_run && (
                  <div className='mt-3 text-xs text-gray-500'>Última execução: {formatDate(collector.last_run)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Configured Collectors Tab */}
      {activeTab === 'configured' && (
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h2 className='text-lg font-medium text-gray-900 dark:text-gray-100'>Coletores Configurados</h2>
          </div>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Nome
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Tipo
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Habilitado
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Última Atualização
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {configuredCollectors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='px-6 py-12 text-center'>
                      <div className='text-gray-500'>
                        <Cog6ToothIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
                        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
                          Nenhum coletor configurado
                        </h3>
                        <p className='text-sm text-gray-500 mb-4'>
                          Configure um coletor na aba "Coletores Disponíveis" para começar a coletar dados.
                        </p>
                        <button
                          onClick={() => setActiveTab('available')}
                          className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700'
                        >
                          Ver Coletores Disponíveis
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  configuredCollectors.map(collector => (
                    <tr key={collector.id} className='hover:bg-gray-50'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div>
                          <div className='text-sm font-medium text-gray-900 dark:text-white'>{collector.name}</div>
                          {collector.description && (
                            <div className='text-sm text-gray-500'>{collector.description}</div>
                          )}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'>
                          {collector.collector_type}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(collector.status)}`}
                        >
                          {collector.status}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            collector.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {collector.enabled ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {formatDate(collector.updated_at)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                        <div className='flex justify-end space-x-2'>
                          {collector.status === 'running' ? (
                            <button
                              onClick={() => handleStopCollector(collector.name)}
                              className='text-red-600 hover:text-red-900'
                              title='Parar'
                            >
                              <PauseIcon className='h-5 w-5' />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStartCollector(collector.name)}
                              className='text-green-600 hover:text-green-900'
                              title='Iniciar'
                            >
                              <PlayIcon className='h-5 w-5' />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewStatus(collector)}
                            className='text-blue-600 hover:text-blue-900'
                            title='Ver Status'
                          >
                            <EyeIcon className='h-5 w-5' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedCollector && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-600'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                Configurar: {selectedCollector.name}
              </h3>
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  setSelectedCollector(null);
                }}
                className='text-gray-400 hover:text-gray-600'
              >
                <XMarkIcon className='h-6 w-6' />
              </button>
            </div>

            <CollectorConfigForm
              collectorType={selectedCollector.type}
              initialConfig={{}}
              onSave={handleSaveConfig}
              onCancel={() => {
                setShowConfigModal(false);
                setSelectedCollector(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && collectorStatus && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-600'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>Status: {collectorStatus.name}</h3>
              <button onClick={() => setShowStatusModal(false)} className='text-gray-400 hover:text-gray-600'>
                <XMarkIcon className='h-6 w-6' />
              </button>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 dark:text-white mb-2'>Status Geral</h4>
                <div className='space-y-2'>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Status:</span>
                    <span className={`text-sm font-medium ${getStatusColor(collectorStatus.status)}`}>
                      {collectorStatus.status}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Executando:</span>
                    <span className='text-sm'>{collectorStatus.is_running ? 'Sim' : 'Não'}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Habilitado:</span>
                    <span className='text-sm'>{collectorStatus.enabled ? 'Sim' : 'Não'}</span>
                  </div>
                </div>
              </div>

              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 dark:text-white mb-2'>Métricas</h4>
                <div className='space-y-2'>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Total de Eventos:</span>
                    <span className='text-sm'>{collectorStatus.metrics.total_events}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Taxa de Sucesso:</span>
                    <span className='text-sm'>{collectorStatus.metrics.success_rate.toFixed(1)}%</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Eventos (24h):</span>
                    <span className='text-sm'>{collectorStatus.metrics.events_last_24h}</span>
                  </div>
                </div>
              </div>

              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 dark:text-white mb-2'>Saúde</h4>
                <div className='space-y-2'>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Status:</span>
                    <span
                      className={`text-sm font-medium ${
                        collectorStatus.health.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {collectorStatus.health.status}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-600'>Última Verificação:</span>
                    <span className='text-sm'>{formatDate(collectorStatus.health.last_check)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectorsNew;
